/**
 * @module app
 */

import { autoinject } from 'aurelia-framework';
import { EnvironmentService } from 'app/shared/services/environment-service';

/**
 * CXOne Chat patch
 * - Always bootstraps CXone loader
 * - Only starts Guide when authenticated
 * - Hard reloads only after an authenticated session existed and user signs out
 */

interface UserInfo {
    isLoggedIn: boolean;
    language: string;
    firstName: string;
    lastName: string;
    email: string;
}

interface CXOneInitOptions {
    logLevel: 'debug' | 'info' | 'warn' | 'error';
}

interface CXOneApi {
    (...args: unknown[]): void;
    q?: Array<unknown[]>;
    u?: string;
    getModuleState?: unknown;
}

@autoinject()
export class CXonePatch {
    protected cxoneName: string = 'cxone';
    protected cxoneBrandId: string | null = null;
    protected cxoneLoaderUrl: string | null = null;

    protected baseUrl: string | null = null;
    protected hadAuthenticatedSession: boolean = false;
    protected isStartingCxone: boolean = false;

    constructor(private readonly env: EnvironmentService) {
        this.cxoneBrandId = this.env.config.externalSystems?.cxone?.brandId || '1008';
        this.cxoneLoaderUrl = 'https://web-modules-de-na2.nicecxone-gov.com/loader/1/loader.js';

        this.baseUrl = this.getBaseUrlUpToProviders(window.location.href);
    }

    protected getBaseUrlUpToProviders(url: string): string | null {
        const marker = '/providers';
        const index = url.toLowerCase().indexOf(marker);
        return index === -1 ? null : url.substring(0, index + marker.length);
    }

    protected ensureCxonePlaceholder(): void {
        window['CXoneDfo'] = this.cxoneName;

        const existing = window[this.cxoneName] as CXOneApi | undefined;
        const placeholder: CXOneApi =
            existing ||
            ((...args: unknown[]): void => {
                placeholder.q = placeholder.q || [];
                placeholder.q.push(args);
            });

        placeholder.u = this.cxoneLoaderUrl || undefined;
        window[this.cxoneName] = placeholder;
    }

    protected ensureCxoneInitQueued(): void {
        if(!window[this.cxoneName]) {
            return;
        }

        const queue = window[this.cxoneName]?.q || [];
        const hasInit = queue.some(args => args[0] === 'init');

        if(!hasInit && !this.isRealCxoneApi()) {
            this.log('Queueing CXone init');
            window[this.cxoneName]?.('init', this.cxoneBrandId, { logLevel: 'debug' } as CXOneInitOptions);
        }
    }

    protected isRealCxoneApi(): boolean {
        return typeof window[this.cxoneName]?.getModuleState === 'function';
    }

    protected addCXOneScript(): void {
        const scriptInDom = !!document.querySelector(`script[src^="${this.cxoneLoaderUrl}"]`);

        if(scriptInDom) {
            this.log('CXone loader already in DOM');
            return;
        }

        this.log('Appending CXone loader script...');
        const script = document.createElement('script');
        script.type = 'module';
        script.src = `${this.cxoneLoaderUrl}?${Math.round(Date.now() / 1000 / 3600)}`;
        document.head.appendChild(script);
        this.log('CXone loader script appended');
    }

    protected async waitForCxoneApi(timeoutMs: number = 10000): Promise<void> {
        const started = Date.now();

        while(!this.isRealCxoneApi()) {
            if(Date.now() - started > timeoutMs) {
                throw new Error('Timed out waiting for CXone loader API');
            }

            await this.delay(100);
        }
    }

    protected async ensureCxoneBootstrapped(): Promise<void> {
        this.ensureCxonePlaceholder();
        this.ensureCxoneInitQueued();
        this.addCXOneScript();

        await this.waitForCxoneApi();
    }

    protected getUserInfo(service: EnvironmentService | null): UserInfo {
        const user = service?.user;

        return {
            isLoggedIn: !!user?.isLoggedIn,
            language: user?.language || service?.tenant?.language || service?.language || 'en',
            firstName: user?.name?.first || '',
            lastName: user?.name?.last || '',
            email: user?.emailAddress || '',
        };
    }

    protected async startSession(userInfo: UserInfo): Promise<void> {
        const { firstName, lastName, email, language } = userInfo;

        if(this.isStartingCxone) {
            this.log('Start already in progress');
            return;
        }

        this.isStartingCxone = true;

        try {
            this.log('Starting Guide session:', userInfo);

            await this.ensureCxoneBootstrapped();

            if(!window[this.cxoneName]) {
                this.log('CXOne API unavailable after bootstrap');
                return;
            }

            // Guide owns the launcher and will initialize chat with the channelId.
            window[this.cxoneName]?.('guide', 'init');
            window[this.cxoneName]?.('chat', 'setCaseCustomField', 'lang', language);
            window[this.cxoneName]?.('chat', 'setCaseCustomField', 'userfirst', firstName);
            window[this.cxoneName]?.('chat', 'setCaseCustomField', 'userlast', lastName);
            window[this.cxoneName]?.('chat', 'setCaseCustomField', 'useremail', email);

            //added for v5 - 5/27/2026
            const initialMessage = `User: ${firstName} ${lastName} - ${email}`;

            //added to v4 of the script - 5/21/2026
            window[this.cxoneName]('guide', 'hidePreSurvey');
            window[this.cxoneName]('chat', 'sendFirstMessageAutomatically', initialMessage);
            window[this.cxoneName]('chat', 'autoStartSession');

            this.hadAuthenticatedSession = true;
            this.log('Session started');
        } finally {
            this.isStartingCxone = false;
        }
    }

    public endSessionAndReload(): void {
        this.log('Ending session...');

        try {
            if(typeof window[this.cxoneName] === 'function') {
                window[this.cxoneName]('chat', 'clearAndResetSession');
                window[this.cxoneName]('chat', 'hideChatWindow');
                window[this.cxoneName]('guide', 'closeMenu');
                window[this.cxoneName]('guide', 'removeTemplate');
            }
        } catch(e) {
            this.log('Error while ending session', e);
        } finally {
            document.querySelector('#cxone-guide-container')?.remove();
            window.location.reload();
        }
    }

    protected ensureLoggedIn(): void {
        setTimeout(() => {
            if(!this.env) {
                return;
            }

            const userInfo = this.getUserInfo(this.env);
            if(!userInfo.isLoggedIn) {
                this.endSessionAndReload();
            }
        }, 1000);
    }

    public async loadCXOne(): Promise<void> {
        await this.ensureCxoneBootstrapped();

        if(!this.env) {
            this.log('No EnvironmentService; leaving CXone bootstrapped but guide not started');
            return;
        }

        const userInfo = this.getUserInfo(this.env);

        if(userInfo.isLoggedIn) {
            await this.startSession(userInfo);
            this.ensureLoggedIn();
            return;
        }

        if(this.hadAuthenticatedSession || document.querySelector('#cxone-guide-container')) {
            this.endSessionAndReload();
            return;
        }

        this.log('Unauthenticated; CXone bootstrapped, guide not started');
    }

    protected delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    protected log(...args: unknown[]): void {
        console.log('Init CXOne:', ...args);
    }
}
